import { paths } from '@/generated/supplier-experience-api';
import { downloadCsvFromPost } from '@/utils/downloadCsvFromPost';
import {
  AdjustmentInvoiceFetchOptions,
  AdjustmentInvoiceRequestBody,
  AdjustmentInvoiceSearchFilters,
} from './useAdjustmentInvoices';
import { TakerMarket } from './useTakerMarkets';

export type AdjustmentInvoicesCsvQuery = NonNullable<paths['/adjustment-invoices/csv']['post']['parameters']['query']>;

export interface DownloadAdjustmentInvoicesCsvArgs {
  makerOrganizationUuid: string;
  takerMarkets: TakerMarket[];
  search?: AdjustmentInvoiceSearchFilters;
  /** Sort only — CSV returns the full filtered set (no cursor/limit). */
  options?: Pick<NonNullable<AdjustmentInvoiceFetchOptions>, 'sortBy' | 'sortDirection'>;
  filename: string;
}

/**
 * Download adjustment invoices CSV via SEA.
 * Body/query match the adjustment list API (same shape as `useAdjustmentInvoices`).
 */
export const downloadAdjustmentInvoicesCsv = async ({
  makerOrganizationUuid,
  takerMarkets,
  search,
  options,
  filename,
}: DownloadAdjustmentInvoicesCsvArgs): Promise<void> => {
  const body = {
    makerOrganizationUuid,
    supplierMarkets: takerMarkets.map((takerMarket) => ({
      marketUuid: takerMarket.marketUuid,
      supplierDivisionUuid: takerMarket.takerDivisionUuid,
    })),
    ...(search ? { search } : {}),
  } satisfies AdjustmentInvoiceRequestBody;

  const query: AdjustmentInvoicesCsvQuery = {
    ...(options?.sortBy && { sortBy: options.sortBy }),
    ...(options?.sortDirection && { sortDirection: options.sortDirection }),
  };

  await downloadCsvFromPost({
    path: 'api/supplier-experience/adjustment-invoices/csv',
    body,
    query: Object.keys(query).length > 0 ? query : undefined,
    filename,
  });
};
